import {
  Alert,
  Button,
  Checkbox,
  Collapse,
  FormControlLabel,
  FormLabel,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { useId, useState } from "react";
import {
  ChristmasImageMethod,
  ChristmasImageOptions,
} from "tng-address-collection-shared/types/portraitsApiTypes.ts";
import { usePortraitsTranslator } from "../i18n/translator";
import { CardPreview } from "./CardPreview";
import { FlowPanel, StepProps } from "./FlowPanel";
import { SinglePortrait } from "./PortraitFrame";
import { ToggleLabel } from "./ToggleLabel";
import { isUnderWay } from "./usePortrait";

const AdvancedOptions = ({
  options,
  onChange,
  disabled,
}: {
  options: ChristmasImageOptions;
  onChange: (options: ChristmasImageOptions) => void;
  disabled: boolean;
}) => {
  const translator = usePortraitsTranslator();
  const methodLabelId = useId();

  return (
    <Stack spacing={1} bgcolor="grey.100" borderRadius={1} padding={2}>
      <FormLabel id={methodLabelId}>{translator("methodLabel")}</FormLabel>
      <ToggleButtonGroup
        exclusive
        fullWidth
        size="small"
        value={options.method}
        disabled={disabled}
        aria-labelledby={methodLabelId}
        onChange={(_, method: ChristmasImageMethod | null) => {
          if (method) {
            onChange({ ...options, method });
          }
        }}
      >
        <ToggleButton value="focused">
          <ToggleLabel
            label={translator("methodFocused")}
            caption={translator("methodFocusedCaption")}
          />
        </ToggleButton>
        <ToggleButton value="broad">
          <ToggleLabel
            label={translator("methodBroad")}
            caption={translator("methodBroadCaption")}
          />
        </ToggleButton>
      </ToggleButtonGroup>
      {options.method === "broad" && (
        <Alert severity="info">{translator("broadEditStabilityHint")}</Alert>
      )}
      <FormControlLabel
        control={
          <Checkbox
            checked={options.outfit}
            onChange={(event) =>
              onChange({ ...options, outfit: event.target.checked })
            }
          />
        }
        label={translator("withOutfit")}
        disabled={disabled}
      />
      <Typography variant="caption" color="text.secondary">
        {translator("advancedOptionsHint")}
      </Typography>
    </Stack>
  );
};

export const ChristmasImageStep = ({
  portrait,
  name,
  flow,
  headingRef,
}: StepProps) => {
  const translator = usePortraitsTranslator();
  const { busy, options, requestChristmasImage } = flow;
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const advancedOptionsId = useId();
  const christmasImage = portrait.christmasImage;
  const imageUrl = christmasImage?.imageUrl;
  const working = requestChristmasImage.isPending || isUnderWay(christmasImage);
  const broad = options.method === "broad";

  const status =
    christmasImage?.status === "queued"
      ? translator("jobQueued")
      : working
        ? translator(
            (christmasImage?.options ?? options).method === "broad"
              ? "christmasImageProcessingBroad"
              : "christmasImageProcessing",
          )
        : undefined;

  const error = requestChristmasImage.isError
    ? translator("unknownErrorTryAgainLater")
    : christmasImage?.status === "failed"
      ? translator("christmasImageFailed")
      : undefined;

  return (
    <FlowPanel
      name={name}
      flow={flow}
      headingRef={headingRef}
      stepTitle={translator("christmasImageTitle")}
      hint={translator("christmasImageHint")}
      picture={
        working || !imageUrl ? (
          <SinglePortrait
            imageUrl={working ? portrait.cutout?.imageUrl : undefined}
            alt={translator("cutoutAlt")}
            emptyLabel={translator("noChristmasImageYet")}
            busy={working}
          />
        ) : (
          <CardPreview
            photoUrl={portrait.cutout?.imageUrl}
            christmasImageUrl={imageUrl}
            autoFlip={false}
            startOnChristmasImage
          />
        )
      }
      status={status}
      error={error}
      actions={
        <>
          <Button
            variant="contained"
            fullWidth
            disabled={busy || !imageUrl}
            onClick={flow.keepChristmasImage}
          >
            {translator("keepChristmasImage")}
          </Button>
          <Button
            variant="outlined"
            fullWidth
            disabled={busy}
            onClick={() => flow.generate(options)}
          >
            {translator(
              imageUrl ? "generateAnother" : "generateChristmasImage",
            )}
            {broad && translator("aboutThirtySeconds")}
          </Button>
          {flow.shownChoice !== "photoOnly" && (
            <Button
              fullWidth
              disabled={busy}
              onClick={flow.goWithoutChristmasImage}
            >
              {translator("noChristmasImageThanks")}
            </Button>
          )}
          <Button
            fullWidth
            aria-expanded={advancedOpen}
            aria-controls={advancedOptionsId}
            onClick={() => setAdvancedOpen(!advancedOpen)}
          >
            {translator(
              advancedOpen ? "hideAdvancedOptions" : "advancedOptions",
            )}
          </Button>
          <Collapse in={advancedOpen} id={advancedOptionsId}>
            <AdvancedOptions
              options={options}
              onChange={flow.setOptions}
              disabled={busy}
            />
          </Collapse>
        </>
      }
    />
  );
};
